import React, { useState } from "react";
import {
    triggerZohoSync, triggerZohoProjectsSync, triggerZohoTasksSync, triggerZohoAssignmentsSync,
    ZohoSyncResult,
} from "../services/api";

type SyncType = "all" | "projects" | "tasks" | "assignments";

const SYNC_LABELS: Record<SyncType, string> = {
    all:         "Sync All",
    projects:    "Projects Only",
    tasks:       "Tasks Only",
    assignments: "Assignments Only",
};

const SYNC_DESCRIPTIONS: Record<SyncType, string> = {
    all:         "Full refresh: projects, tasks, and staff assignments.",
    projects:    "Project metadata only (name, customer, billing type, PM, active/whitelist status) — fast, skips tasks and assignments.",
    tasks:       "Tasks and billing rates for every active project already in this app.",
    assignments: "Staff assignments and billing rates for every active project already in this app.",
};

const cardStyle: React.CSSProperties = {
    border: "1px solid #e0e0e0", borderRadius: "6px", padding: "20px",
    backgroundColor: "white", marginBottom: "28px", maxWidth: "640px",
};
const sectionTitleStyle: React.CSSProperties = {
    color: "#5C5D5E", fontSize: "16px", fontWeight: "bold", margin: "0 0 6px 0",
};
const descStyle: React.CSSProperties = {
    color: "#666", fontSize: "13px", margin: "0 0 16px 0", lineHeight: 1.5,
};
const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 20px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
    display: "flex", alignItems: "center", gap: "8px",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd", borderRadius: "4px",
    padding: "9px 18px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
    display: "flex", alignItems: "center", gap: "8px",
};

const Spinner: React.FC<{ dark?: boolean }> = ({ dark }) => (
    <>
        <style>{"@keyframes zs-spin { to { transform: rotate(360deg); } }"}</style>
        <div style={{
            width: "14px", height: "14px", borderRadius: "50%",
            border: dark ? "2px solid #5C5D5E44" : "2px solid #ffffff88",
            borderTopColor: dark ? "#5C5D5E" : "#fff",
            animation: "zs-spin 0.8s linear infinite", display: "inline-block",
        }} />
    </>
);

const formatTimestamp = (iso: string): string => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
};

const ZohoSync: React.FC = () => {
    const [syncingType, setSyncingType] = useState<SyncType | null>(null);
    const [lastType, setLastType] = useState<SyncType | null>(null);
    const [result, setResult] = useState<ZohoSyncResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const runSync = async (type: SyncType) => {
        setSyncingType(type);
        setError(null);
        try {
            let res: ZohoSyncResult;
            if (type === "all")               res = await triggerZohoSync();
            else if (type === "projects")     res = await triggerZohoProjectsSync();
            else if (type === "tasks")        res = await triggerZohoTasksSync();
            else                              res = await triggerZohoAssignmentsSync();

            setResult(res);
            setLastType(type);
        } catch (e: any) {
            const message = e?.response?.data?.detail || `${SYNC_LABELS[type]} sync failed. Please try again.`;
            setError(message);
        } finally {
            setSyncingType(null);
        }
    };

    const disabled = syncingType !== null;

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>Zoho Sync</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "24px", fontSize: "13px" }}>
                Pull the latest data from Zoho into this app.
            </p>

            <div style={cardStyle}>
                <div style={sectionTitleStyle}>Manual Sync</div>
                <p style={descStyle}>
                    A full sync runs automatically every night at 2:00 AM Central — these buttons
                    are mainly for pulling in a change (like a new PM assignment or task rate) right
                    away instead of waiting for the next nightly run. Use the individual options when
                    you only need one part refreshed quickly.
                </p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    <button
                        onClick={() => runSync("all")}
                        disabled={disabled}
                        style={{ ...primaryBtnStyle, opacity: disabled ? 0.7 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                    >
                        {syncingType === "all" && <Spinner />}
                        {syncingType === "all" ? "Syncing…" : "Sync All"}
                    </button>

                    {(["projects", "tasks", "assignments"] as SyncType[]).map(type => (
                        <button
                            key={type}
                            onClick={() => runSync(type)}
                            disabled={disabled}
                            style={{ ...secondaryBtnStyle, opacity: disabled ? 0.7 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                        >
                            {syncingType === type && <Spinner dark />}
                            {syncingType === type ? "Syncing…" : SYNC_LABELS[type]}
                        </button>
                    ))}
                </div>

                {lastType && !error && (
                    <p style={{ fontSize: "12px", color: "#999", margin: "10px 0 0 0" }}>
                        {SYNC_DESCRIPTIONS[lastType]}
                    </p>
                )}

                {error && (
                    <div style={{ color: "#C00000", fontSize: "13px", marginTop: "14px" }}>{error}</div>
                )}

                {result && !error && (
                    <div style={{
                        marginTop: "16px", padding: "12px 14px", backgroundColor: "#FDE8BC",
                        borderRadius: "4px", fontSize: "13px", color: "#5C5D5E",
                    }}>
                        <div style={{ fontWeight: "bold", marginBottom: "6px" }}>
                            {lastType ? SYNC_LABELS[lastType] : "Sync"} completed at {formatTimestamp(result.summary.synced_at)}
                        </div>
                        {result.summary.projects_created !== undefined && (
                            <div>Projects created: {result.summary.projects_created}</div>
                        )}
                        {result.summary.projects_updated !== undefined && (
                            <div>Projects updated: {result.summary.projects_updated}</div>
                        )}
                        {result.summary.projects_deactivated !== undefined && (
                            <div>Projects deactivated: {result.summary.projects_deactivated}</div>
                        )}
                        {result.summary.projects_checked !== undefined && (
                            <div>Active projects checked: {result.summary.projects_checked}</div>
                        )}
                        {result.summary.tasks_synced !== undefined && (
                            <div>Tasks synced: {result.summary.tasks_synced}</div>
                        )}
                        {result.summary.assignments_synced !== undefined && (
                            <div>Assignments synced: {result.summary.assignments_synced}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ZohoSync;
